import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Bell, ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';

/**
 * The phone's top bar, the same on every app screen (a bot chat brings its own).
 *
 * - LEFT: on Home the day and the greeting; on the other bottom-tab screens their
 *   name as a large title; on every other screen a back arrow and the screen's name.
 * - RIGHT: Search · Notifications · Account, 40pt tap targets, one red dot
 *   (not a count) for unread notifications.
 * - Height is 52pt plus the status bar / notch inset, so in the iPhone and
 *   Android apps the bar sits under the clock instead of being squeezed by it.
 */
export default function MobileHeader({
  title,
  subtitle,
  home,
  root,
  onSearchClick,
  onBellClick,
  onAvatarClick,
  onBackClick,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();

  let unreadCount = 0;
  try {
    const ctx = useNotifications();
    unreadCount = ctx?.unreadCount || 0;
  } catch {
    /* context may not be mounted on the very first render */
  }

  const iconButton = 'h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-full text-foreground active:bg-accent/70 hover:bg-accent/40';

  return (
    <header
      className="md:hidden flex-shrink-0 sticky top-0 z-30 bg-background/90 backdrop-blur-xl border-b border-border/50"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="mobile-header"
    >
      <div className="h-[52px] flex items-center gap-1 px-2">
        {home ? (
          <div className="pl-2 min-w-0 flex flex-col justify-center">
            <span className="text-[12px] leading-tight text-muted-foreground truncate">{subtitle}</span>
            <h1 className="text-[19px] leading-tight font-semibold tracking-tight text-foreground truncate">{title}</h1>
          </div>
        ) : root ? (
          <h1 className="pl-2 min-w-0 truncate text-[22px] leading-tight font-bold tracking-tight text-foreground">
            {title}
          </h1>
        ) : (
          <>
            <button
              type="button"
              onClick={onBackClick}
              className={`${iconButton} -mr-1`}
              aria-label={t('common.back')}
              data-testid="mobile-back"
            >
              <ChevronLeft className="w-[26px] h-[26px]" strokeWidth={2.25} />
            </button>
            <h1 className="min-w-0 truncate text-[17px] leading-tight font-semibold text-foreground">
              {title}
            </h1>
          </>
        )}

        <div className="flex-1" />

        <div className="flex items-center">
          <button type="button" onClick={onSearchClick} aria-label={t('nav.search')} className={iconButton}>
            <Search className="w-[22px] h-[22px]" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={onBellClick}
            aria-label={t('notifications.title')}
            className={`relative ${iconButton}`}
          >
            <Bell className="w-[22px] h-[22px]" strokeWidth={2} />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 ring-2 ring-background" />
            )}
          </button>

          <button
            type="button"
            onClick={onAvatarClick}
            aria-label={t('nav.more')}
            className={iconButton}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-black font-semibold text-[13px]">
                {(user?.full_name || t('common.user')).charAt(0)}
              </div>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
